import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Calendar, Clock, Info, Plus, Trash2, Video } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";

const DAY_NAMES = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const PROVIDER_OPTS = [["meet", "Google Meet"], ["teams", "Microsoft Teams"], ["phone", "Phone"], ["in_person", "In person"], ["other", "Other video link"]];
const STATUS_LABELS = { awaiting_candidate: "Awaiting candidate response", scheduled: "Confirmed", reschedule_requested: "Reschedule requested", awaiting_reconfirmation: "Awaiting reconfirmation", completed: "Completed", no_show: "No-show", declined: "Declined", cancelled: "Cancelled" };
const PILL_STYLES = { awaiting_candidate: "bg-amber-50 text-amber-700", scheduled: "bg-green-50 text-green-700", reschedule_requested: "bg-orange-50 text-orange-700", awaiting_reconfirmation: "bg-amber-50 text-amber-700", completed: "bg-blue-50 text-blue-700", no_show: "bg-red-50 text-red-700", declined: "bg-zinc-100 text-zinc-500", cancelled: "bg-zinc-100 text-zinc-400" };
const EMPTY_FORM = { application_id: "", mode: "direct", provider: "meet", date: "", time: "", duration_minutes: 45, link: "", location: "", interviewer_ids: [], kit_id: "", note: "", recording_requested: false };

function StatusPill({ s }) {
  return (
    <span className={`text-[11px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ${PILL_STYLES[s] || "bg-zinc-100 text-zinc-500"}`}
      data-testid={`iv-status-${s}`}>
      {STATUS_LABELS[s] || s}
    </span>
  );
}

export default function EmployerInterviews() {
  const [items, setItems] = useState([]);
  const [settings, setSettings] = useState(null);
  const [kits, setKits] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [team, setTeam] = useState([]);
  const [dlg, setDlg] = useState(false);
  const [resched, setResched] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState("");
  const [kitForm, setKitForm] = useState({ name: "", questions: "" });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [showArchived, setShowArchived] = useState(false);

  const load = useCallback(() => {
    Promise.all([
      api.get("/employer/interviews", { params: { include_archived: showArchived } }).then((r) => setItems(r.data)).catch(() => {}),
      api.get("/employer/interview-settings").then((r) => setSettings(r.data)).catch(() => {}),
      api.get("/employer/interview-kits").then((r) => setKits(r.data)).catch(() => {}),
      api.get("/employer/candidates").then((r) => setCandidates(r.data)).catch(() => {}),
      api.get("/employer/team").then((r) => setTeam(Array.isArray(r.data) ? r.data : (r.data.members || r.data.team || []))).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, [showArchived]);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.application_id) { toast.error("Choose an application"); return; }
    setSaving("dlg");
    try {
      const payload = { application_id: form.application_id, mode: form.mode, provider: form.provider,
        duration_minutes: form.duration_minutes, link: form.link, location: form.location,
        interviewer_ids: form.interviewer_ids, note: form.note, recording_requested: form.recording_requested };
      if (form.kit_id) payload.kit_id = form.kit_id;
      if (form.mode === "direct") {
        if (!form.date || !form.time) { toast.error("Pick a date and time"); setSaving(""); return; }
        payload.at = new Date(`${form.date}T${form.time}:00`).toISOString();
      }
      await api.post("/employer/interviews", payload);
      toast.success(form.mode === "direct" ? "Interview scheduled and candidate emailed." : "Invitation sent, the candidate picks a time.");
      setDlg(false);
      setForm(EMPTY_FORM);
      load();
    } catch (err) { toast.error(formatApiError(err)); } finally { setSaving(""); }
  };

  const submitReschedule = async (e) => {
    e.preventDefault();
    if (!resched?.date || !resched?.time) { toast.error("Pick a new date and time"); return; }
    setSaving("resched");
    try {
      const r = await api.put(`/employer/interviews/${resched.id}`, { at: new Date(`${resched.date}T${resched.time}:00`).toISOString() });
      toast.success(r.data?.interview?.status === "awaiting_reconfirmation"
        ? "Interview moved, the candidate has been asked to reconfirm."
        : "Interview moved, the candidate has been emailed.");
      setResched(null);
      load();
    } catch (err) { toast.error(formatApiError(err)); } finally { setSaving(""); }
  };

  const cancelIv = async (iv) => {
    if (!window.confirm(`Cancel the interview with ${iv.candidate_name}?`)) return;
    try {
      await api.post(`/employer/interviews/${iv.id}/cancel`, {});
      toast.success("Interview cancelled.");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const resendIv = async (iv) => {
    try {
      await api.post(`/employer/interviews/${iv.id}/resend`, {});
      toast.success("Interview details resent to the candidate.");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const archiveIv = async (iv, archived) => {
    try {
      await api.post(`/employer/interviews/${iv.id}/archive`, { archived });
      toast.success(archived ? "Interview archived." : "Interview restored.");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const markAttendance = async (iv, attended) => {
    try {
      await api.post(`/employer/interviews/${iv.id}/attendance`, { attended });
      toast.success(attended ? "Marked as completed." : "Marked as no-show.");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const saveSettings = async () => {
    setSaving("settings");
    try {
      await api.put("/employer/interview-settings", settings);
      toast.success("Availability saved.");
    } catch (err) { toast.error(formatApiError(err)); } finally { setSaving(""); }
  };

  const saveKit = async (e) => {
    e.preventDefault();
    setSaving("kit");
    try {
      await api.post("/employer/interview-kits", {
        name: kitForm.name,
        questions: kitForm.questions.split("\n").map((q) => q.trim()).filter(Boolean),
      });
      toast.success("Interview kit saved.");
      setKitForm({ name: "", questions: "" });
      load();
    } catch (err) { toast.error(formatApiError(err)); } finally { setSaving(""); }
  };

  const setWindow = (day, field, value) => {
    setSettings((s) => {
      const days = { ...s.days };
      const rows = [...(days[String(day)] || [])];
      if (!rows.length) rows.push({ start: "09:00", end: "17:00" });
      rows[0] = { ...rows[0], [field]: value };
      days[String(day)] = rows;
      return { ...s, days };
    });
  };

  const toggleDay = (day, on) => {
    setSettings((s) => {
      const days = { ...s.days };
      days[String(day)] = on ? [{ start: "09:00", end: "17:00" }] : [];
      return { ...s, days };
    });
  };

  if (loading) {
    return (
      <div className="py-16 text-center" data-testid="iv-loading">
        <div className="h-8 w-8 border-2 border-zinc-300 border-t-zinc-900 rounded-full animate-spin mx-auto" />
      </div>
    );
  }

  const q = search.trim().toLowerCase();
  const visible = items.filter((i) => {
    if (statusFilter && i.status !== statusFilter) return false;
    if (q && !`${i.candidate_name} ${i.job_title} ${i.company_name}`.toLowerCase().includes(q)) return false;
    return true;
  });
  const awaiting = visible.filter((i) => ["awaiting_candidate", "awaiting_reconfirmation"].includes(i.status));
  const upcoming = visible.filter((i) => ["scheduled", "reschedule_requested"].includes(i.status));
  const past = visible.filter((i) => ["completed", "no_show", "declined", "cancelled"].includes(i.status));

  const lastActivity = (iv) => {
    const h = iv.history || [];
    if (!h.length) return null;
    const last = h[h.length - 1];
    const who = last.by === "candidate" ? "the candidate" : last.by;
    const when = last.at ? new Date(last.at).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "";
    return `${(last.action || "").replace(/_/g, " ")}${who ? ` by ${who}` : ""}${when ? ` · ${when}` : ""}`;
  };

  const IvCard = ({ iv, past: isPast }) => (
    <div className="bg-white border border-zinc-200 rounded-md p-4 flex flex-wrap items-center gap-3" data-testid={`iv-row-${iv.id}`}>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <Link to={`/interviews/${iv.id}`} data-testid={`iv-open-${iv.id}`}
            className="font-semibold text-zinc-950 hover:text-blue-700 transition-colors">{iv.candidate_name}</Link>
          <StatusPill s={iv.status} />
          {iv.archived && (
            <span className="text-[11px] font-semibold text-zinc-500 bg-zinc-100 border border-zinc-200 px-2 py-0.5 rounded-full" data-testid={`iv-archived-badge-${iv.id}`}>Archived</span>
          )}
          {iv.round > 1 && <span className="text-[11px] text-zinc-400">Round {iv.round}</span>}
        </div>
        <p className="text-sm text-zinc-500 mt-0.5">{iv.job_title} · {iv.when_str || "time not set"} · {iv.duration_minutes} min · {iv.provider_label}</p>
        {iv.location ? <p className="text-xs text-zinc-400 mt-0.5">{iv.location}</p> : null}
        {lastActivity(iv) && (
          <p className="text-xs text-zinc-400 mt-1" data-testid={`iv-activity-${iv.id}`}>Last activity: {lastActivity(iv)}</p>
        )}
      </div>
      {!isPast && (
        <div className="flex items-center gap-2 shrink-0">
          {iv.status === "scheduled" && new Date(iv.at) < new Date() && (
            <>
              <button onClick={() => markAttendance(iv, true)} data-testid={`iv-attended-${iv.id}`}
                className="h-8 px-3 text-xs font-semibold bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors">Attended</button>
              <button onClick={() => markAttendance(iv, false)} data-testid={`iv-noshow-${iv.id}`}
                className="h-8 px-3 text-xs font-semibold border border-zinc-300 rounded-md hover:border-red-400 hover:text-red-600 transition-colors">No-show</button>
            </>
          )}
          {iv.status === "scheduled" && iv.link && iv.link.startsWith("https://") && (
            <a href={iv.link} target="_blank" rel="noreferrer" data-testid={`iv-join-${iv.id}`}
              className="h-8 px-3 inline-flex items-center gap-1.5 text-xs font-semibold bg-zinc-950 text-white rounded-md hover:bg-zinc-800 transition-colors">
              <Video className="h-3.5 w-3.5" /> Join interview
            </a>
          )}
          {iv.status === "reschedule_requested" && (
            <button onClick={async () => {
                try {
                  await api.post(`/employer/interviews/${iv.id}/decline-reschedule`);
                  toast.success("Reschedule declined, the original time stands. The candidate has been emailed.");
                  load();
                } catch (err) { toast.error(formatApiError(err)); }
              }} data-testid={`iv-decline-reschedule-${iv.id}`}
              className="h-8 px-3 text-xs font-semibold border border-zinc-300 rounded-md hover:border-zinc-500 transition-colors">Keep original time</button>
          )}
          <button onClick={() => resendIv(iv)} data-testid={`iv-resend-${iv.id}`}
            className="h-8 px-3 text-xs font-semibold border border-zinc-300 rounded-md hover:border-zinc-500 transition-colors">Resend</button>
          <button onClick={() => setResched({ id: iv.id, date: "", time: "" })} data-testid={`iv-reschedule-${iv.id}`}
            className="h-8 px-3 text-xs font-semibold border border-zinc-300 rounded-md hover:border-zinc-500 transition-colors">Reschedule</button>
          <button onClick={() => cancelIv(iv)} data-testid={`iv-cancel-${iv.id}`}
            className="h-8 px-3 text-xs font-semibold border border-zinc-300 rounded-md hover:border-red-400 hover:text-red-600 transition-colors">Cancel</button>
          {iv.archived ? (
            <button onClick={() => archiveIv(iv, false)} data-testid={`iv-unarchive-${iv.id}`}
              className="h-8 px-3 text-xs font-semibold border border-zinc-300 rounded-md hover:border-green-500 hover:text-green-700 transition-colors">Restore</button>
          ) : (
            <button onClick={() => archiveIv(iv, true)} data-testid={`iv-archive-${iv.id}`}
              className="h-8 px-3 text-xs font-semibold border border-zinc-300 rounded-md hover:border-zinc-500 transition-colors">Archive</button>
          )}
        </div>
      )}
      {isPast && (
        <div className="flex items-center gap-2 shrink-0">
          {iv.attendance && (
            <span className="text-xs text-zinc-500">
              {iv.attendance.attended ? "Attended" : "No-show"} · marked {new Date(iv.attendance.at).toLocaleDateString("en-GB")}
            </span>
          )}
          {iv.archived ? (
            <button onClick={() => archiveIv(iv, false)} data-testid={`iv-unarchive-${iv.id}`}
              className="h-8 px-3 text-xs font-semibold border border-zinc-300 rounded-md hover:border-green-500 hover:text-green-700 transition-colors">Restore</button>
          ) : (
            <button onClick={() => archiveIv(iv, true)} data-testid={`iv-archive-${iv.id}`}
              className="h-8 px-3 text-xs font-semibold border border-zinc-300 rounded-md hover:border-zinc-500 transition-colors">Archive</button>
          )}
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-8" data-testid="iv-tab">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <h2 className="font-display text-2xl font-bold text-zinc-950">Interviews</h2>
          <span className="group relative inline-flex" data-testid="iv-info">
            <Info className="h-4 w-4 text-zinc-400 cursor-help" />
            <span className="pointer-events-none absolute left-0 top-full z-20 mt-2 w-64 rounded-md bg-zinc-950 px-3 py-2 text-xs leading-relaxed text-white opacity-0 transition-opacity duration-150 group-hover:opacity-100">Schedule directly or let candidates pick a time from your availability. Meet, Teams or any link.</span>
          </span>
        </div>
        <button onClick={() => setDlg(true)} data-testid="iv-schedule-open"
          className="inline-flex items-center gap-2 bg-zinc-950 text-white text-sm font-semibold px-5 py-2.5 rounded-md hover:bg-zinc-800 transition-colors">
          <Plus className="h-4 w-4" /> Schedule interview
        </button>
      </div>

      <div className="flex flex-col sm:flex-row gap-2.5" data-testid="iv-filters">
        <input value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder="Search candidate or job" data-testid="iv-search"
          className="flex-1 h-10 px-3 border border-zinc-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-600" />
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="iv-status-filter"
          className="h-10 px-3 border border-zinc-300 rounded-md text-sm bg-white">
          <option value="">All statuses</option>
          {Object.entries(STATUS_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <label className="flex items-center gap-2 text-xs font-medium text-zinc-500 cursor-pointer sm:ml-auto" data-testid="iv-show-archived">
          <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} className="h-4 w-4 accent-zinc-900" />
          Show archived
        </label>
      </div>

      {awaiting.length > 0 && (
        <section>
          <h3 className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-3">Awaiting response ({awaiting.length})</h3>
          <div className="space-y-3">{awaiting.map((iv) => <IvCard key={iv.id} iv={iv} />)}</div>
        </section>
      )}

      <section>
        <h3 className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-3">Upcoming ({upcoming.length})</h3>
        {upcoming.length === 0 ? (
          <div className="border border-dashed border-zinc-300 rounded-md p-10 text-center" data-testid="iv-empty">
            <Calendar className="h-8 w-8 text-zinc-300 mx-auto mb-2" />
            <p className="text-sm text-zinc-500">No interviews yet. Schedule one from here or from any candidate card.</p>
          </div>
        ) : (
          <div className="space-y-3">{upcoming.map((iv) => <IvCard key={iv.id} iv={iv} />)}</div>
        )}
      </section>

      {past.length > 0 && (
        <section>
          <h3 className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-3">Past ({past.length})</h3>
          <div className="space-y-3">{past.map((iv) => <IvCard key={iv.id} iv={iv} past />)}</div>
        </section>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {settings && (
          <section className="bg-white border border-zinc-200 rounded-lg p-5 sm:p-6" data-testid="iv-settings">
            <h3 className="font-display text-lg font-bold text-zinc-950 mb-1 flex items-center gap-2"><Clock className="h-4 w-4" /> Weekly availability</h3>
            <p className="text-xs text-zinc-500 mb-4">Candidates self-schedule inside these windows.</p>
            <div className="space-y-2">
              {DAY_NAMES.map((name, d) => {
                const win = (settings.days?.[String(d)] || [])[0];
                return (
                  <div key={name} className="flex items-center gap-2" data-testid={`iv-day-${d}`}>
                    <label className="flex items-center gap-2 w-32 text-sm text-zinc-700 cursor-pointer">
                      <input type="checkbox" checked={!!win} onChange={(e) => toggleDay(d, e.target.checked)}
                        data-testid={`iv-day-toggle-${d}`} className="h-4 w-4 accent-zinc-900" />
                      {name}
                    </label>
                    {win ? (
                      <>
                        <input type="time" value={win.start} onChange={(e) => setWindow(d, "start", e.target.value)}
                          data-testid={`iv-day-start-${d}`} className="h-8 px-2 border border-zinc-300 rounded-md text-sm" />
                        <span className="text-zinc-400 text-xs">to</span>
                        <input type="time" value={win.end} onChange={(e) => setWindow(d, "end", e.target.value)}
                          data-testid={`iv-day-end-${d}`} className="h-8 px-2 border border-zinc-300 rounded-md text-sm" />
                      </>
                    ) : (
                      <span className="text-xs text-zinc-400">Unavailable</span>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="grid grid-cols-2 gap-3 mt-4">
              <label className="text-xs text-zinc-500">Slot steps (min)
                <input type="number" min="15" max="120" value={settings.slot_minutes}
                  onChange={(e) => setSettings({ ...settings, slot_minutes: parseInt(e.target.value || "30", 10) })}
                  data-testid="iv-slot-minutes" className="mt-1 w-full h-9 px-2 border border-zinc-300 rounded-md text-sm" />
              </label>
              <label className="text-xs text-zinc-500">Interview length (min)
                <input type="number" min="15" max="180" value={settings.duration_minutes}
                  onChange={(e) => setSettings({ ...settings, duration_minutes: parseInt(e.target.value || "45", 10) })}
                  data-testid="iv-duration" className="mt-1 w-full h-9 px-2 border border-zinc-300 rounded-md text-sm" />
              </label>
              <label className="text-xs text-zinc-500">Minimum notice (hours)
                <input type="number" min="1" max="168" value={settings.notice_hours}
                  onChange={(e) => setSettings({ ...settings, notice_hours: parseInt(e.target.value || "24", 10) })}
                  data-testid="iv-notice" className="mt-1 w-full h-9 px-2 border border-zinc-300 rounded-md text-sm" />
              </label>
              <label className="text-xs text-zinc-500">Days ahead shown
                <input type="number" min="1" max="42" value={settings.horizon_days}
                  onChange={(e) => setSettings({ ...settings, horizon_days: parseInt(e.target.value || "14", 10) })}
                  data-testid="iv-horizon" className="mt-1 w-full h-9 px-2 border border-zinc-300 rounded-md text-sm" />
              </label>
            </div>
            <button onClick={saveSettings} disabled={saving === "settings"} data-testid="iv-settings-save"
              className="mt-4 h-9 px-4 bg-zinc-950 text-white text-sm font-semibold rounded-md hover:bg-zinc-800 disabled:opacity-50 transition-colors">
              {saving === "settings" ? "Saving…" : "Save availability"}
            </button>
          </section>
        )}

        <section className="bg-white border border-zinc-200 rounded-lg p-5 sm:p-6" data-testid="iv-kits">
          <h3 className="font-display text-lg font-bold text-zinc-950 mb-1">Interview kits</h3>
          <p className="text-xs text-zinc-500 mb-4">Reusable question sets your team sees in the interview room.</p>
          {kits.length > 0 && (
            <ul className="space-y-2 mb-4">
              {kits.map((k) => (
                <li key={k.id} className="flex items-center justify-between gap-2 border border-zinc-200 rounded-md px-3 py-2" data-testid={`iv-kit-${k.id}`}>
                  <span className="text-sm font-medium text-zinc-800">{k.name} <span className="text-zinc-400 font-normal">· {k.questions.length} questions</span></span>
                  <button onClick={async () => { await api.delete(`/employer/interview-kits/${k.id}`); load(); }}
                    data-testid={`iv-kit-delete-${k.id}`} className="text-zinc-400 hover:text-red-600 transition-colors">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </li>
              ))}
            </ul>
          )}
          <form onSubmit={saveKit} className="space-y-2">
            <input value={kitForm.name} onChange={(e) => setKitForm({ ...kitForm, name: e.target.value })}
              placeholder="Kit name (e.g. Driver phone screen)" data-testid="iv-kit-name"
              className="w-full h-10 px-3 border border-zinc-300 rounded-md text-sm" />
            <textarea value={kitForm.questions} onChange={(e) => setKitForm({ ...kitForm, questions: e.target.value })}
              placeholder="One question per line" rows={4} data-testid="iv-kit-questions"
              className="w-full px-3 py-2 border border-zinc-300 rounded-md text-sm" />
            <button disabled={saving === "kit"} data-testid="iv-kit-save"
              className="h-9 px-4 border border-zinc-300 text-sm font-semibold rounded-md hover:border-zinc-500 disabled:opacity-50 transition-colors">
              {saving === "kit" ? "Saving…" : "Save kit"}
            </button>
          </form>
        </section>
      </div>

      {dlg && (
        <div className="fixed inset-0 z-[80] bg-black/40 flex items-center justify-center p-4" onClick={() => setDlg(false)} data-testid="iv-dialog">
          <div className="bg-white rounded-lg max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display text-xl font-bold text-zinc-950 mb-4">Schedule interview</h3>
            <form onSubmit={submit} className="space-y-3">
              <select value={form.application_id} onChange={(e) => setForm({ ...form, application_id: e.target.value })}
                data-testid="ivd-application" className="w-full h-10 px-3 border border-zinc-300 rounded-md text-sm bg-white">
                <option value="">Choose an application…</option>
                {candidates.map((c) => (
                  <option key={c.id} value={c.id}>{c.name} · {c.job_title} ({c.status})</option>
                ))}
              </select>
              <div className="grid grid-cols-2 gap-2">
                <button type="button" onClick={() => setForm({ ...form, mode: "direct" })} data-testid="ivd-mode-direct"
                  className={`h-10 text-sm font-semibold rounded-md border transition-colors ${form.mode === "direct" ? "bg-zinc-950 text-white border-zinc-950" : "border-zinc-300 text-zinc-600"}`}>
                  Set the time myself
                </button>
                <button type="button" onClick={() => setForm({ ...form, mode: "invite" })} data-testid="ivd-mode-invite"
                  className={`h-10 text-sm font-semibold rounded-md border transition-colors ${form.mode === "invite" ? "bg-zinc-950 text-white border-zinc-950" : "border-zinc-300 text-zinc-600"}`}>
                  Candidate picks a time
                </button>
              </div>
              {form.mode === "direct" && (
                <div className="grid grid-cols-2 gap-2">
                  <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })}
                    data-testid="ivd-date" className="h-10 px-3 border border-zinc-300 rounded-md text-sm" />
                  <input type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })}
                    data-testid="ivd-time" className="h-10 px-3 border border-zinc-300 rounded-md text-sm" />
                </div>
              )}
              <select value={form.provider} onChange={(e) => setForm({ ...form, provider: e.target.value })}
                data-testid="ivd-provider" className="w-full h-10 px-3 border border-zinc-300 rounded-md text-sm bg-white">
                {PROVIDER_OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
              {["meet", "teams", "other"].includes(form.provider) && (
                <input value={form.link} onChange={(e) => setForm({ ...form, link: e.target.value })}
                  placeholder="Meeting link (https://…)" data-testid="ivd-link"
                  className="w-full h-10 px-3 border border-zinc-300 rounded-md text-sm" />
              )}
              {form.provider === "in_person" && (
                <input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })}
                  placeholder="Interview location" data-testid="ivd-location"
                  className="w-full h-10 px-3 border border-zinc-300 rounded-md text-sm" />
              )}
              <label className="block text-xs text-zinc-500">Length (minutes)
                <input type="number" min="15" max="180" value={form.duration_minutes}
                  onChange={(e) => setForm({ ...form, duration_minutes: parseInt(e.target.value || "45", 10) })}
                  data-testid="ivd-duration" className="mt-1 w-28 h-10 px-3 border border-zinc-300 rounded-md text-sm" />
              </label>
              {team.length > 1 && (
                <div>
                  <p className="text-xs text-zinc-500 mb-1.5">Interviewers</p>
                  <div className="space-y-1.5" data-testid="ivd-interviewers">
                    {team.map((m) => {
                      const id = m.id || m._id || m.user_id;
                      const on = form.interviewer_ids.includes(id);
                      return (
                        <label key={id} className="flex items-center gap-2 text-sm text-zinc-700 cursor-pointer">
                          <input type="checkbox" checked={on} data-testid={`ivd-interviewer-${id}`}
                            onChange={(e) => setForm({ ...form, interviewer_ids: e.target.checked ? [...form.interviewer_ids, id] : form.interviewer_ids.filter((x) => x !== id) })}
                            className="h-4 w-4 accent-zinc-900" />
                          {m.name} <span className="text-xs text-zinc-400">{m.email}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}
              {kits.length > 0 && (
                <select value={form.kit_id} onChange={(e) => setForm({ ...form, kit_id: e.target.value })}
                  data-testid="ivd-kit" className="w-full h-10 px-3 border border-zinc-300 rounded-md text-sm bg-white">
                  <option value="">No interview kit</option>
                  {kits.map((k) => <option key={k.id} value={k.id}>{k.name}</option>)}
                </select>
              )}
              <textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })}
                placeholder="Note for your team (not shown to the candidate)" rows={2} data-testid="ivd-note"
                className="w-full px-3 py-2 border border-zinc-300 rounded-md text-sm" />
              <label className="flex items-center gap-2 text-sm text-zinc-700 cursor-pointer">
                <input type="checkbox" checked={form.recording_requested}
                  onChange={(e) => setForm({ ...form, recording_requested: e.target.checked })}
                  data-testid="ivd-recording" className="h-4 w-4 accent-zinc-900" />
                I may record this interview <span className="text-xs text-zinc-400">(candidate consent is collected first)</span>
              </label>
              <div className="flex gap-2 pt-1">
                <button type="submit" disabled={saving === "dlg"} data-testid="ivd-submit"
                  className="flex-1 h-11 bg-zinc-950 text-white text-sm font-semibold rounded-md hover:bg-zinc-800 disabled:opacity-50 transition-colors">
                  {saving === "dlg" ? "Scheduling…" : form.mode === "direct" ? "Schedule and email candidate" : "Send invitation"}
                </button>
                <button type="button" onClick={() => setDlg(false)} data-testid="ivd-cancel"
                  className="h-11 px-4 border border-zinc-300 text-sm font-semibold rounded-md hover:border-zinc-500 transition-colors">
                  Close
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {resched && (
        <div className="fixed inset-0 z-[80] bg-black/40 flex items-center justify-center p-4" onClick={() => setResched(null)} data-testid="iv-resched-dialog">
          <div className="bg-white rounded-lg max-w-sm w-full p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display text-lg font-bold text-zinc-950 mb-4">Reschedule interview</h3>
            <form onSubmit={submitReschedule} className="space-y-3">
              <input type="date" value={resched.date} onChange={(e) => setResched({ ...resched, date: e.target.value })}
                data-testid="ivr-date" className="w-full h-10 px-3 border border-zinc-300 rounded-md text-sm" />
              <input type="time" value={resched.time} onChange={(e) => setResched({ ...resched, time: e.target.value })}
                data-testid="ivr-time" className="w-full h-10 px-3 border border-zinc-300 rounded-md text-sm" />
              <button type="submit" disabled={saving === "resched"} data-testid="ivr-submit"
                className="w-full h-11 bg-zinc-950 text-white text-sm font-semibold rounded-md hover:bg-zinc-800 disabled:opacity-50 transition-colors">
                {saving === "resched" ? "Saving…" : "Move interview"}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
